import React, { useContext, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faIndustry, faGlobe, faMapMarkerAlt, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { getActors } from '../functions';
import { LanguageContext } from '../config/multilingualContext';
import MultiLingualContent from '../config/multilingualContent';
import { endpointImg } from '../api';
import '../assets/css/actor.css';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';

const Actors = () => {
  const { language } = useContext(LanguageContext);
  const [actors, setActors] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      try {
        const data = await getActors();
        setActors(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error('Failed to load actors', e);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  const getLabel = (item) => {
    return {
      french: item?.label_fr ?? item?.name ?? '',
      english: item?.label_en ?? item?.name ?? '',
      portuguais: item?.label_pr ?? item?.name ?? '',
      swahili: item?.label_sw ?? item?.name ?? '',
    }[language] || item?.label_fr || '';
  };

  const getSector = (item) => {
    return {
      french: item?.sector_fr ?? item?.sector ?? '',
      english: item?.sector_en ?? item?.sector ?? '',
      portuguais: item?.sector_pr ?? item?.sector ?? '',
      swahili: item?.sector_sw ?? item?.sector ?? '',
    }[language] || item?.sector || '';
  };

  const getCountry = (item) => {
    return {
      french: item?.country.label_fr ?? item?.country ?? '',
      english: item?.country.label_en ?? item?.country ?? '',
      portuguais: item?.country.label_pr ?? item?.country ?? '',
      swahili: item?.country.label_sw ?? item?.country ?? '',
    }[language] || item?.country.label_fr || '';
  };

  const getSubActivity = (item) => {
    return {
      french: item?.sub_activities_label_fr ?? '',
      english: item?.sub_activities_label_en ?? '',
      portuguais: item?.sub_activities_label_pr ?? '',
      swahili: item?.sub_activities_label_sw ?? '',
    }[language] || item?.sub_activities_label_en || '';
  };

  // Group actors by sub-activity label for the current language
  const grouped = actors.reduce((acc, item) => {
    const key = getSubActivity(item) || getSector(item) || 'Autres';
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});

  const groups = Object.entries(grouped);
  const [activeIdx, setActiveIdx] = useState(0);

  return (
    <div className="news-modern-container" id="actors">
      <div className="news-modern-wrapper">
        <div className="news-header">
          <h2><MultiLingualContent contentID="actorsTitle" /></h2>
          <p className="news-subtitle">
            <MultiLingualContent contentID="actorsSubtitle" />
          </p>
        </div>

        {isLoading ? (
          <div className="news-loading">
            {[1, 2, 3].map(i => (
              <div key={i} className="news-skeleton">
                <div className="skeleton-image"></div>
                <div className="skeleton-content">
                  <div className="skeleton-title"></div>
                  <div className="skeleton-text"></div>
                  <div className="skeleton-text"></div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          groups.length > 0 ? (
            <>
              <div className="actors-tabs">
                {groups.map(([title], idx) => (
                  <button
                    key={title || idx}
                    className={`actors-tab ${idx === activeIdx ? 'active' : ''}`}
                    onClick={() => setActiveIdx(idx)}
                    type="button"
                  >
                    {title || 'Autres'}
                  </button>
                ))}
              </div>
              {(() => {
                const [groupTitle, groupItems] = groups[activeIdx] || ['Autres', []];
                const prevCls = `.actors-prev-${activeIdx}`;
                const nextCls = `.actors-next-${activeIdx}`;
                return (
                  <div className="actors-group-section">
                    <div className="actors-swiper-wrapper">
                      <div className={`actors-swiper-nav nav-${activeIdx}`}>
                        <button className={`slick-arrow prev-arrow actors-prev-${activeIdx}`} aria-label="Previous">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M15 18l-6-6 6-6"/>
                          </svg>
                        </button>
                        <button className={`slick-arrow next-arrow actors-next-${activeIdx}`} aria-label="Next">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M9 18l6-6-6-6"/>
                          </svg>
                        </button>
                      </div>
                      <Swiper
                        modules={[Navigation]}
                        navigation={{ prevEl: prevCls, nextEl: nextCls }}
                        spaceBetween={16}
                        slidesPerView={1}
                        breakpoints={{
                          640: { slidesPerView: 2, spaceBetween: 16 },
                          1024: { slidesPerView: 3, spaceBetween: 20 },
                          1400: { slidesPerView: 4, spaceBetween: 24 },
                        }}
                      >
                        {groupItems.map((item, index) => (
                          <SwiperSlide key={item?.id ?? `actor-${activeIdx}-${index}`}>
                            <div className="news-card">
                              {/*<div className="news-image">
                                <img src={item.logo ? endpointImg + item.logo : '/company-placeholder.png'} alt={getLabel(item)} />
                              </div>*/}
                              <div className="news-content">
                                <h3 className="news-title h5 fw-semibold">{getLabel(item)}</h3>
                                <p className="news-excerpt">
                                  {getSector(item)}
                                </p>
                                <div className="news-meta">
                                  {item.country && (
                                    <div className="meta-item">
                                      <FontAwesomeIcon icon={faMapMarkerAlt} className="meta-icon" />
                                      <span>{getCountry(item)}</span>
                                    </div>
                                  )}
                                </div>
                                {item.website && (
                                  <a className="read-more-btn" href={item.website} target="_blank" rel="noopener noreferrer">
                                    <MultiLingualContent contentID="visitWebsite" />
                                    <FontAwesomeIcon icon={faExternalLinkAlt} className="read-more-icon" />
                                  </a>
                                )}
                              </div>
                            </div>
                          </SwiperSlide>
                        ))}
                      </Swiper>
                    </div>
                  </div>
                );
              })()}
            </>
          ) : (
            <div className="empty-state">
              <FontAwesomeIcon icon={faIndustry} className="empty-icon" />
              <h3 className="empty-message"><MultiLingualContent contentID="noActorsFound" /></h3>
              <p className="empty-submessage"><MultiLingualContent contentID="checkBackLater" /></p>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default Actors;